import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  Divider,
  alpha,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface ViewAnalyticsData {
  id: number;
  company_id: number;
  company_name: string;
  lot: string | null;
  status: string;
  share_token: string;
  view_count: number;
  last_viewed_at: string | null;
  created_at: string;
}

interface AnalyticsSummary {
  total_views: number;
  programs_with_views: number;
  unique_companies: number;
  most_viewed_program: ViewAnalyticsData | null;
  recently_viewed: ViewAnalyticsData[];
  all_programs: ViewAnalyticsData[];
}

const StatCard = ({ 
  title, 
  value, 
  icon, 
  color 
}: { 
  title: string; 
  value: number | string; 
  icon: React.ReactNode; 
  color: string;
}) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mb: 0.5, fontWeight: 500 }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box 
            sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              backgroundColor: alpha(color, 0.15),
              color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function ViewAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { translations: t } = useTranslation();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/metrics/view_analytics`, {
        headers: {
          'Authorization': token || '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const data = await response.json();
      setAnalytics(data);
      setError('');
    } catch (err: any) {
      setError(err.message || t.viewAnalytics.error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_started':
        return 'default';
      case 'in_progress':
        return 'primary';
      case 'completed':
        return 'success';
      case 'delivered':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t.viewAnalytics.neverViewed;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRowClick = (programId: number) => {
    navigate(`/programs/${programId}`);
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          gap: 2
        }}
      >
        <CircularProgress size={48} />
        <Typography color="text.secondary">{t.viewAnalytics.loading}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700, 
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}
        >
          {t.viewAnalytics.title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t.viewAnalytics.subtitle}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(4, 1fr)' 
          },
          gap: 2,
          mb: 4,
        }}
      >
        <StatCard
          title={t.viewAnalytics.totalViews}
          value={analytics.total_views.toLocaleString()}
          icon={<VisibilityIcon sx={{ fontSize: 28 }} />}
          color={theme.palette.primary.main}
        />
        <StatCard
          title={t.viewAnalytics.programsShared}
          value={analytics.programs_with_views}
          icon={<ShareIcon sx={{ fontSize: 28 }} />}
          color={theme.palette.success.main}
        />
        <StatCard
          title={t.viewAnalytics.companiesReached}
          value={analytics.unique_companies}
          icon={<BusinessIcon sx={{ fontSize: 28 }} />}
          color={theme.palette.info.main}
        />
        <StatCard
          title={t.viewAnalytics.mostViewed}
          value={analytics.most_viewed_program?.view_count || 0}
          icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
          color={theme.palette.warning.main}
        />
      </Box>

      {/* Most Viewed Program Highlight */}
      {analytics.most_viewed_program && analytics.most_viewed_program.view_count > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <TrendingUpIcon color="warning" />
              {t.viewAnalytics.mostViewed}
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: { xs: 'flex-start', sm: 'center' },
                p: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.warning.main, 0.15),
                }
              }}
              onClick={() => handleRowClick(analytics.most_viewed_program!.id)}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t.viewAnalytics.program} #{analytics.most_viewed_program.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {analytics.most_viewed_program.company_name}
                  {analytics.most_viewed_program.lot && ` • ${t.viewAnalytics.lot}: ${analytics.most_viewed_program.lot}`}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography variant="h5" fontWeight={700} color="warning.main">
                  {analytics.most_viewed_program.view_count.toLocaleString()} {t.viewAnalytics.views}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(analytics.most_viewed_program.last_viewed_at)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* All Programs Table */}
      <Card>
        <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Typography 
            variant="h6" 
            sx={{ mb: 2, fontWeight: 600, px: { xs: 1, sm: 0 } }}
          >
            {t.viewAnalytics.recentlyViewed}
          </Typography>

          {analytics.all_programs.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <VisibilityIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                {t.viewAnalytics.noViews}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Desktop Table */}
              <TableContainer 
                component={Paper} 
                variant="outlined" 
                sx={{ display: { xs: 'none', md: 'block' } }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>{t.viewAnalytics.program}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.viewAnalytics.company}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.viewAnalytics.lot}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.viewAnalytics.status}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>{t.viewAnalytics.viewCount}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.viewAnalytics.lastViewedAt}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.all_programs.map((program) => (
                      <TableRow 
                        key={program.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleRowClick(program.id)}
                      >
                        <TableCell>
                          <Typography fontWeight={500}>#{program.id}</Typography>
                        </TableCell>
                        <TableCell>{program.company_name}</TableCell>
                        <TableCell>{program.lot || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(program.status)}
                            color={getStatusColor(program.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={program.view_count.toLocaleString()}
                            color={program.view_count > 0 ? 'primary' : 'default'}
                            size="small"
                            variant={program.view_count > 0 ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color={program.last_viewed_at ? 'text.primary' : 'text.disabled'}
                          >
                            {formatDate(program.last_viewed_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Mobile Cards */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {analytics.all_programs.map((program, index) => (
                  <Card 
                    key={program.id}
                    variant="outlined"
                    sx={{ 
                      mb: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      }
                    }}
                    onClick={() => handleRowClick(program.id)}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {t.viewAnalytics.program} #{program.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {program.company_name}
                          </Typography>
                        </Box>
                        <Chip
                          icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                          label={program.view_count.toLocaleString()}
                          color={program.view_count > 0 ? 'primary' : 'default'}
                          size="small"
                          variant={program.view_count > 0 ? 'filled' : 'outlined'}
                        />
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t.viewAnalytics.lot}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {program.lot || '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t.viewAnalytics.status}
                          </Typography>
                          <Chip
                            label={getStatusLabel(program.status)}
                            color={getStatusColor(program.status)}
                            size="small"
                          />
                        </Box>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t.viewAnalytics.lastViewedAt}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            fontWeight={500}
                            color={program.last_viewed_at ? 'text.primary' : 'text.disabled'}
                          >
                            {formatDate(program.last_viewed_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}


